import { AgentSessionLabel, TASK_FIT_DIMENSIONS } from '../../../api/agent-miner-client/agent-miner-client.type';
import type { TaskFitCell, TaskFitDimension } from '../../../api/agent-miner-client/agent-miner-client.type';

/** The dimension used when the URL carries no (or an unrecognized) value. */
const DEFAULT_TASK_FIT_DIMENSION: TaskFitDimension = 'raw_type';

/**
 * Known dimension values, used to validate a hand-edited URL param. Built from
 * {@link TASK_FIT_DIMENSIONS} — the same tuple `TaskFitDimension` is derived
 * from — so a new dimension value can't silently miss this validation set.
 */
const TASK_FIT_DIMENSION_VALUES = new Set<string>(TASK_FIT_DIMENSIONS);

/** Known metric values, the single source of truth `TaskFitMetric` is derived from. */
const TASK_FIT_METRICS = ['rework', 'cycle_time', 'cost'] as const;

/** The per-cell metric the heatmap can color and sort by. */
type TaskFitMetric = (typeof TASK_FIT_METRICS)[number];

/** The metric used when the URL carries no (or an unrecognized) value. */
const DEFAULT_TASK_FIT_METRIC: TaskFitMetric = 'rework';

/** Known metric values, used to validate a hand-edited URL param. */
const TASK_FIT_METRIC_VALUES = new Set<string>(TASK_FIT_METRICS);

/**
 * Display names for Flora's session labels, reusing the {@link AgentSessionLabel}
 * enum so the matrix rows read the same as the Sessions tab's label filter when
 * grouping by Flora label. The backend also sends a `label`, which callers fall
 * back to for any value not covered here (e.g. a raw-type dimension row).
 */
const FLORA_LABEL_DISPLAY_NAMES: Record<AgentSessionLabel, string> = {
  [AgentSessionLabel.BugFix]: 'Bug fix',
  [AgentSessionLabel.FeatureBuild]: 'Feature build',
  [AgentSessionLabel.Refactor]: 'Refactor',
  [AgentSessionLabel.TestingCoverage]: 'Testing coverage',
  [AgentSessionLabel.Investigation]: 'Investigation',
  [AgentSessionLabel.Documentation]: 'Documentation',
  [AgentSessionLabel.Devops]: 'DevOps',
  [AgentSessionLabel.CodeReview]: 'Code review',
  [AgentSessionLabel.Unclassified]: 'Unclassified',
};

/**
 * One flattened matrix row for the shared `<Table>`, in the TRANSPOSED layout:
 * one row per MODEL FAMILY (the y-axis), carrying its `model_family` label and
 * `provider`, plus each task type keyed by its own `row_key` so a task-type
 * column can address its cell through `keyof MatrixRow`. The index signature is
 * what lets a dynamic task-type key serve as a `TableColumn.key`; a cell is
 * `null` when the payload carries no entry for that (model, task type) pairing.
 */
type MatrixRow = {
  model_family: string;
  provider: string;
} & Record<string, TaskFitCell | string | null>;

export {
  DEFAULT_TASK_FIT_DIMENSION,
  DEFAULT_TASK_FIT_METRIC,
  FLORA_LABEL_DISPLAY_NAMES,
  TASK_FIT_DIMENSION_VALUES,
  TASK_FIT_METRICS,
  TASK_FIT_METRIC_VALUES,
};
export type { MatrixRow, TaskFitMetric };
